/* ----------------------------------------------------------------------------
 * WordPress Connector — pure view/model layer (no network, no import.meta).
 *
 * This module is deliberately DEPENDENCY-FREE so it can be unit-tested under
 * `node --test --experimental-strip-types` exactly like data/active-domain.ts
 * and data/voicePace.ts (the repo's no-jsdom convention: test the pure model
 * that the thin React view renders, not the DOM). The fetch layer lives beside
 * it in data/connections.ts (which imports lib/api).
 *
 * Contract source of truth:
 *   docs/brain/product/public-wordpress-connector-architecture-and-build-plan-2026-07-21.md
 *   bookedcentral-api/modules/connectors/Http/ConnectorPairRoutes.php  (POST /v1/connector/pairings
 *     -> { pairing_token, expires_in })
 *   bookedcentral-api/modules/connectors/sql/bc_publish_targets.sql    (connection row shape)
 *
 * SECURITY INVARIANT: the pairing token is single-use + ~10-min TTL and is meant
 * to be shown once in the authed UI. The per-connection HMAC SECRET / key_id /
 * auth_ref are NEVER returned to this UI and must NEVER be rendered — the row
 * model reads a whitelist of safe fields, so a secret can never leak into a view
 * even if a mis-built server put one on the wire. See SECRET_FIELD_NAMES + the
 * connections.test.ts "no secret is ever displayed" locks.
 * -------------------------------------------------------------------------- */

// ---- Endpoints (the ONLY connector endpoints this lane is authorised to use) ----
// Paths are relative to the API base ("/v1"); lib/api prefixes the origin.
export const CONNECTOR_ENDPOINTS = {
  /** POST — mint a single-use pairing token for the caller's workspace. */
  pairings: '/connector/pairings',
  /** GET — list this workspace's connected sites. */
  connections: '/connector/connections',
  /** DELETE — revoke one connection (kills secret + disables the target). */
  connection: (id: number | string): string => `/connector/connections/${id}`,
  /** GET — the connected site's EXISTING categories + a best-match suggestion for
   *  a topic (?topic=). Read-only; used to pick the category BEFORE a draft push. */
  connectionCategories: (id: number | string): string => `/connector/connections/${id}/categories`,
  /** POST — push an APPROVED (ready) blog draft to a connected site as a
   *  WordPress DRAFT. Draft-only: the server hard-codes WP status draft. */
  publications: '/connector/publications',
  /** GET (browser navigation) — download the installable Connector plugin. */
  pluginDownload: 'https://bookedcentral.com/connectors/wordpress/download',
} as const;

/** Backend default TTL (PairingService::TTL_SECONDS). Used only if the server
 *  omits expires_in. ~10 minutes per the security model. */
export const PAIRING_TTL_FALLBACK_SECONDS = 600;

// ---- Wire shapes ------------------------------------------------------------

/** POST /v1/connector/pairings response (ConnectorPairRoutes::handleIssue). */
export interface PairingTokenDto {
  pairing_token: string;
  expires_in: number;
}

/** One connected site (GET /v1/connector/connections item). Derived from
 *  bc_publish_targets; carries NO secret (auth_ref/secret/key_id never sent). */
export interface ConnectionDto {
  id: number;
  platform?: string;
  site_label?: string;
  site_url?: string;
  status?: string; // 'active' | 'disabled'
  capabilities?: Record<string, unknown> | null;
  allow_scheduled_publish?: boolean;
  publish_mode?: string; // 'draft' | 'publish'
  category?: string | null;
  author?: string | null;
  connected_at?: string | null; // ISO-8601 UTC (bc_publish_targets.created_at)
  last_pushed_at?: string | null;
  /** the BC SEO domain (bc_seo_projects.id) this site is assigned to, or null
   *  when unassigned (server-authoritative; null = shows under "Unassigned"). */
  project_id?: number | null;
}

// ---- Pairing view -----------------------------------------------------------

export interface ConnectStep {
  n: number;
  title: string;
  detail: string;
  /** present on the download step — an href the user follows. */
  href?: string;
  /** present on the final step — the token the user pastes into the plugin. */
  token?: string;
}

export interface PairingView {
  token: string;
  steps: ConnectStep[];
  /** human "expires in ~10 minutes" hint. */
  expiresLabel: string;
  /** absolute ms epoch at which the token stops working. */
  expiresAtMs: number;
  /** the flow always offers a re-generate (tokens are cheap + single-use). */
  canRegenerate: true;
}

/** Human label for a TTL in seconds — "~10 minutes", "~30 seconds". */
export function expiresLabel(expiresInSeconds: number): string {
  const s = Math.max(0, Math.floor(expiresInSeconds));
  if (s >= 60) {
    const m = Math.round(s / 60);
    return `Expires in ~${m} minute${m === 1 ? '' : 's'}`;
  }
  return `Expires in ~${s} second${s === 1 ? '' : 's'}`;
}

/** The three-step connect checklist. Step 3 embeds the token to paste. */
export function buildConnectSteps(token: string, pluginDownloadHref: string): ConnectStep[] {
  return [
    {
      n: 1,
      title: 'Download the BookedCentral Connector plugin',
      detail: 'A standard installable WordPress plugin — no code, no server access needed.',
      href: pluginDownloadHref,
    },
    {
      n: 2,
      title: 'Install and activate it on your WordPress site',
      detail: 'In WordPress: Plugins → Add New → Upload Plugin → choose the .zip → Activate.',
    },
    {
      n: 3,
      title: 'Paste this token into the plugin’s “Connect” field',
      detail: 'Open Settings → BookedCentral in your WordPress admin and paste the pairing token.',
      token,
    },
  ];
}

/** Assemble everything the Connect panel renders from a freshly-issued token. */
export function buildPairingView(input: {
  dto: PairingTokenDto;
  pluginDownloadHref: string;
  issuedAtMs: number;
}): PairingView {
  const ttl =
    Number.isFinite(input.dto.expires_in) && input.dto.expires_in > 0
      ? input.dto.expires_in
      : PAIRING_TTL_FALLBACK_SECONDS;
  return {
    token: input.dto.pairing_token,
    steps: buildConnectSteps(input.dto.pairing_token, input.pluginDownloadHref),
    expiresLabel: expiresLabel(ttl),
    expiresAtMs: input.issuedAtMs + ttl * 1000,
    canRegenerate: true,
  };
}

/** Has a shown token passed its TTL? Drives the "expired — regenerate" state. */
export function pairingIsExpired(view: Pick<PairingView, 'expiresAtMs'>, nowMs: number): boolean {
  return nowMs >= view.expiresAtMs;
}

// ---- Connection row model ---------------------------------------------------

export type StatusTone = 'success' | 'neutral' | 'warning';

/** Human labels for the connector capability flags (bc_publish_targets.capabilities_json). */
const CAPABILITY_LABELS: Record<string, string> = {
  push_drafts: 'Push drafts',
  read_bookings: 'Read bookings',
  yoast_meta: 'Yoast SEO meta',
  cta_meta: 'CTA meta',
  featured_media: 'Featured images',
  category_term_ids: 'Categories',
  tag_term_ids: 'Tags',
};

/** Field names that must NEVER appear in a rendered connection. The row model
 *  reads a whitelist, so these are dropped even if a server erroneously sent one. */
export const SECRET_FIELD_NAMES = [
  'secret',
  'key_id',
  'auth_ref',
  'auth_user',
  'password',
  'hmac',
  'api_key',
  'apikey',
  'token',
  'pairing_token',
] as const;

export interface ConnectionRow {
  id: number;
  label: string;
  url: string;
  statusLabel: string;
  statusTone: StatusTone;
  capabilityLabels: string[];
  connectedLabel: string;
  publishMode: PublishMode;
  /** assigned BC SEO domain id, or null when unassigned. */
  projectId: number | null;
}

function truthyCapability(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.length > 0;
  return v !== null && v !== undefined && v !== '' && v !== 0 && v !== '0';
}

/** Deterministic UTC date label (locale-independent for tests): "2026-07-21". */
export function formatConnectedDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Map a wire ConnectionDto to the exact fields the table renders. Only known,
 * safe fields are read — a secret-shaped field on the input can never reach the
 * output (security invariant; locked by tests).
 */
export function connectionRowModel(dto: ConnectionDto): ConnectionRow {
  const status = (dto.status ?? 'active').toLowerCase();
  const statusTone: StatusTone = status === 'active' ? 'success' : status === 'disabled' ? 'neutral' : 'warning';
  const caps = dto.capabilities ?? {};
  const capabilityLabels = Object.entries(caps)
    .filter(([, v]) => truthyCapability(v))
    .map(([k]) => CAPABILITY_LABELS[k] ?? k)
    .filter((label) => !SECRET_FIELD_NAMES.includes(label as (typeof SECRET_FIELD_NAMES)[number]));

  return {
    id: dto.id,
    label: dto.site_label?.trim() || dto.site_url || `Site ${dto.id}`,
    url: dto.site_url ?? '',
    statusLabel: status === 'active' ? 'Connected' : status === 'disabled' ? 'Disabled' : status,
    statusTone,
    capabilityLabels,
    connectedLabel: formatConnectedDate(dto.connected_at),
    publishMode: publishModeOf(dto),
    projectId: normalizeProjectId(dto.project_id),
  };
}

// ---- domain (per-site) association ------------------------------------------

/** Coerce a wire project_id to a positive int or null (0/''/negative -> null). */
export function normalizeProjectId(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/** Minimal domain shape the panel passes (a subset of DomainOption). */
export interface DomainChoiceOption {
  projectId: number;
  hostname: string;
  displayName?: string;
}

/** The sentinel value used by the row Domain dropdown for "no domain". */
export const UNASSIGNED_DOMAIN_VALUE = '' as const;

/** Human label for a site's assigned domain: "Unassigned" when null, the domain's
 *  "display · host" (or host) when known, or a stable fallback for an id whose
 *  domain is not in the caller's list (e.g. a domain removed from the workspace). */
export function connectionDomainLabel(
  projectId: number | null,
  domains: readonly DomainChoiceOption[],
): string {
  if (projectId == null) return 'Unassigned';
  const d = domains.find((x) => x.projectId === projectId);
  if (!d) return `Domain #${projectId}`;
  const name = (d.displayName ?? '').trim();
  return name && name !== d.hostname ? `${name} · ${d.hostname}` : d.hostname;
}

// ---- list filtering (per-domain, auto-follows the "Viewing" domain) ---------

/** The Connected-sites list filter: a concrete domain id, the workspace-wide
 *  "All domains" lens, or the "Unassigned" bucket (sites not tied to a domain). */
export type ConnectionsFilter = number | 'all' | 'unassigned';

/**
 * The list filter derived from the shell's current "Viewing" domain. The list
 * AUTO-FOLLOWS the active domain (shows only that domain's sites) instead of
 * defaulting to "All domains"; the workspace-wide lens (isAllDomains) or an
 * unresolved/absent active domain fall back to 'all'. "All domains" therefore
 * stays a MANUAL override the user can still pick — never the silent default
 * while a single domain is being viewed.
 */
export function viewingFilterFor(input: {
  isAllDomains: boolean;
  activeProjectId: number | null;
}): ConnectionsFilter {
  if (input.isAllDomains) return 'all';
  return input.activeProjectId != null ? input.activeProjectId : 'all';
}

/**
 * Is a connection (by its assigned domain id, null = Unassigned) visible under a
 * given list filter? 'all' shows everything; 'unassigned' shows only sites with
 * no domain; a concrete id shows ONLY the sites assigned to exactly that domain —
 * so a site assigned to domain A is hidden while Viewing an unrelated domain B.
 */
export function connectionVisibleUnder(projectId: number | null, filter: ConnectionsFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'unassigned') return projectId == null;
  return projectId === filter;
}

// ---- Publish mode (draft-by-default, double-gated) --------------------------

export type PublishMode = 'draft' | 'publish';

/** Draft-only is the platform default and the safe fallback (design §7). */
export const PUBLISH_MODE_DEFAULT: PublishMode = 'draft';

/** Double-gated warning shown BEFORE a user may switch a site to publish. */
export const PUBLISH_MODE_WARNING =
  'Switching to Publish lets BookedCentral publish content live on your site. ' +
  'This is double-gated: it also requires publishing to be enabled per-site by an owner. ' +
  'Until both gates are open, everything stays a Draft you review in WordPress first.';

/** Resolve a DTO to its effective publish mode. Anything that is not an explicit,
 *  fully-enabled publish falls back to draft (fail-safe). */
export function publishModeOf(dto: Pick<ConnectionDto, 'publish_mode' | 'allow_scheduled_publish'>): PublishMode {
  if (dto.publish_mode === 'publish' && dto.allow_scheduled_publish === true) return 'publish';
  return PUBLISH_MODE_DEFAULT;
}

/** True only when a change actually escalates draft -> publish (needs confirm). */
export function requiresPublishConfirm(from: PublishMode, to: PublishMode): boolean {
  return from === 'draft' && to === 'publish';
}

export interface PublishModeModel {
  mode: PublishMode;
  isDraft: boolean;
  /** the warning to surface — only populated when the mode is (or is going) publish. */
  warning: string | null;
}

export function publishModeModel(mode: PublishMode): PublishModeModel {
  return {
    mode,
    isDraft: mode === 'draft',
    warning: mode === 'publish' ? PUBLISH_MODE_WARNING : null,
  };
}
